import { useState, useRef, useCallback } from 'react';
import { API_BASE } from '../services/apiClient';

/**
 * Hook for intelligence-layer agent chat SSE streaming.
 * Mirrors useSelfHelpChat but hits the /agent/ endpoints and skips citations.
 */
export function useAgentChat({ getToken }) {
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [error, setError] = useState(null);

  const abortRef = useRef(null);
  const textBuffer = useRef('');

  const sendMessage = useCallback(
    async (sessionId, content) => {
      setIsStreaming(true);
      setStreamingText('');
      setError(null);
      textBuffer.current = '';

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const token = typeof getToken === 'function' ? await getToken() : null;
        const url = `${API_BASE}/api/chatbot/agent/sessions/${sessionId}/messages`;

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ content }),
          signal: controller.signal,
        });

        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          throw new Error(errBody.error || `HTTP ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let buffer = '';
        let messageId = null;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          let hadTextDelta = false;

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const jsonStr = line.slice(6);
            if (!jsonStr) continue;

            try {
              const event = JSON.parse(jsonStr);
              if (event.type === 'text_delta' && event.text) {
                textBuffer.current += event.text;
                hadTextDelta = true;
              } else if (event.type === 'done') {
                messageId = event.messageId;
              } else if (event.type === 'error') {
                setError(event.error);
              }
            } catch {
              // Ignore malformed SSE
            }
          }

          if (hadTextDelta) {
            setStreamingText(textBuffer.current);
            await new Promise((r) => setTimeout(r, 0));
          }
        }

        setStreamingText(textBuffer.current);
        return { text: textBuffer.current, messageId, citations: [] };
      } catch (err) {
        if (err.name === 'AbortError') return null;
        const msg = err instanceof Error ? err.message : 'Unknown error';
        setError(msg);
        return null;
      } finally {
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    [getToken],
  );

  const cancelStream = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setIsStreaming(false);
  }, []);

  return {
    isStreaming,
    streamingText,
    streamingCitations: [],
    error,
    sendMessage,
    cancelStream,
  };
}
